import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, ChevronLeft, ChevronRight, ListFilter, Loader2, Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { FilterChip } from '../../../../components/design-system/common/FilterChip';
import { useProductsApi } from '../../../../common/hooks/use-products-api.hook';
import { useVendorFamilies } from '../../../../common/hooks/use-vendor-families.hook';
import { useResolveTranslation } from '../../../../common/hooks/use-resolve-translation.hook';
import { CatalogFacets, EMPTY_CATALOG_FACETS } from '../../../../common/models/catalog-query.model';
import { toCanvasProduct } from '../product-mapping';

interface PickerProduct {
    uuid: string;
    name: string;
    sku?: string;
    thumbnailPath?: string;
    visualizationType?: string;
    applicationTypes?: string[];
    modelPath?: string;
    texturePath?: string;
    surfaceTexturePath?: string;
    /** Top-level category slug (e.g. 'rugs') — flags flat floor coverings. */
    category?: string;
    /**
     * Extra product gallery images — rendered under the product node as
     * clickable thumbnails that swap the main node thumbnail, and
     * forwarded as additional reference images to the worker for
     * multi-view generations.
     */
    galleryImages?: string[];
}

interface ProductPickerPanelProps {
    open: boolean;
    onClose: () => void;
    /** Multi-select "Add to canvas" button */
    onDrop: (products: PickerProduct[]) => void;
    /** Single product drag-dropped onto canvas (screen coords) */
    onDragDropSingle?: (product: PickerProduct, screenX: number, screenY: number) => void;
    /** When true, the panel is in "replace" mode — button says "Replace product" */
    replacing?: boolean;
    /**
     * Where it opens. 'start' (default): at the start edge of its container.
     * 'beside-panel': next to a 400px side panel at the end edge (the Studio
     * shot panel), so the pick happens beside the button that asked for it;
     * on a phone, where that panel covers the screen, above it.
     */
    anchor?: 'start' | 'beside-panel';
}

const BESIDE_PANEL_PX = 400;

const PAGE_SIZE = 24;

/**
 * Slides in from the left — shows the vendor catalog with thumbnails,
 * debounced search, pagination, and multi-select. Clicking "Drop to canvas"
 * emits the selected products as a single group to be rendered as one
 * Product node.
 *
 * Pages and search come from the server, as in the catalogue: downloading
 * every product up front stalled the panel for large vendors. Selection is
 * kept by uuid, so it survives paging and searching.
 */
export const ProductPickerPanel: React.FC<ProductPickerPanelProps> = ({
    open,
    onClose,
    onDrop,
    onDragDropSingle,
    replacing,
    anchor = 'start',
}) => {
    const { t } = useTranslation();
    const productsApi = useProductsApi();

    const [loading, setLoading] = useState(false);
    const [pageItems, setPageItems] = useState<PickerProduct[]>([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState<Record<string, PickerProduct>>({});

    // ── Filters — the catalogue's own, asked of the same endpoints ──
    const { families } = useVendorFamilies();
    const { rt } = useResolveTranslation();
    const [familyId, setFamilyId] = useState<string | undefined>(undefined);
    const [productTypes, setProductTypes] = useState<string[]>([]);
    const [collections, setCollections] = useState<string[]>([]);
    const [facets, setFacets] = useState<CatalogFacets>(EMPTY_CATALOG_FACETS);
    const filterCount = productTypes.length + collections.length;

    // Options follow the family: surface types are no use while browsing furniture.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        productsApi.getCatalogFacets({ familyId, draft: 'complete' })
            .then((next) => { if (!cancelled) setFacets(next); })
            .catch(() => { /* the filters stay empty; search still works */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, familyId]);

    const chooseFamily = (id?: string) => {
        setFamilyId(id);
        setProductTypes([]);
        setCollections([]);
    };
    const toggleIn = (setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) =>
        setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

    // Debounce search
    useEffect(() => {
        const h = setTimeout(() => setDebouncedSearch(search), 250);
        return () => clearTimeout(h);
    }, [search]);

    useEffect(() => { setPage(1); }, [debouncedSearch, familyId, productTypes, collections]);

    // Fetch the visible page. Only the latest request may write, so a slow
    // page cannot land over the one the vendor has since moved to.
    const requestRef = useRef(0);
    useEffect(() => {
        if (!open) return;
        const request = ++requestRef.current;
        setLoading(true);
        (async () => {
            try {
                const { products, total: count } = await productsApi.findProductsPage({
                    page,
                    limit: PAGE_SIZE,
                    search: debouncedSearch,
                    draft: 'complete',
                    familyId,
                    productTypes,
                    collections,
                });
                if (request !== requestRef.current) return;
                setPageItems((products || []).map(toCanvasProduct));
                setTotal(count);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Failed to load products', err);
            } finally {
                if (request === requestRef.current) setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, page, debouncedSearch, familyId, productTypes, collections]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const toggleSelect = useCallback((product: PickerProduct) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[product.uuid]) delete next[product.uuid];
            else next[product.uuid] = product;
            return next;
        });
    }, []);

    const selectedCount = Object.keys(selected).length;

    // ── Custom mouse-based drag (replaces native HTML5 drag) ────
    const [dragProduct, setDragProduct] = useState<PickerProduct | null>(null);
    const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
    const dragStartRef = useRef({ x: 0, y: 0, started: false });

    const handleCardMouseDown = useCallback((e: React.MouseEvent, product: PickerProduct) => {
        if (e.button !== 0) return;
        e.preventDefault();
        dragStartRef.current = { x: e.clientX, y: e.clientY, started: false };

        const handleMouseMove = (ev: MouseEvent) => {
            const dx = ev.clientX - dragStartRef.current.x;
            const dy = ev.clientY - dragStartRef.current.y;
            if (!dragStartRef.current.started && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
                dragStartRef.current.started = true;
                setDragProduct(product);
                document.body.style.cursor = 'grabbing';
                document.body.style.userSelect = 'none';
            }
            if (dragStartRef.current.started) {
                setDragPos({ x: ev.clientX, y: ev.clientY });
            }
        };

        const handleMouseUp = (ev: MouseEvent) => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';

            if (dragStartRef.current.started) {
                setDragProduct(null);
                // Check if dropped on canvas — emit via onDragDropSingle
                if (onDragDropSingle) {
                    onDragDropSingle(product, ev.clientX, ev.clientY);
                }
            } else {
                // No drag happened — treat as click (toggle select)
                toggleSelect(product);
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [onDragDropSingle, toggleSelect]);

    // Click-outside-to-close via document listener (no backdrop div —
    // a backdrop overlay was intercepting button clicks inside the panel)
    const panelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Element;
            if (target.closest?.('[data-picker-filter-menu]')) return;
            if (panelRef.current && !panelRef.current.contains(target)) {
                onClose();
            }
        };
        // Delay attachment so the opening click doesn't immediately close
        const timer = setTimeout(() => document.addEventListener('mousedown', handler), 50);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handler);
        };
    }, [open, onClose]);

    return (
        <>
        <AnimatePresence>
            {open && (
                <motion.div
                    ref={panelRef}
                    initial={{ x: anchor === 'beside-panel' ? 24 : -400, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: anchor === 'beside-panel' ? 24 : -400, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 36 }}
                    style={anchor === 'beside-panel'
                        ? (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
                            ? { position: 'fixed', top: 72, bottom: 16, insetInlineEnd: BESIDE_PANEL_PX + 12, width: 380, zIndex: 40 }
                            : { position: 'fixed', inset: 12, zIndex: 60 })
                        : {
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            bottom: 16,
                            width: 'min(380px, calc(100% - 120px))',
                            maxHeight: 'calc(100% - 32px)',
                            zIndex: 30,
                        }}
                >
                    <div className="flex h-full flex-col overflow-hidden rounded-xl border bg-background">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b px-4 py-3">
                            <div>
                                <p className="font-heading text-sm font-medium tracking-tight text-foreground">
                                    {t('CONTENT_STUDIO.PICK_PRODUCTS', 'Pick products')}
                                </p>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    {t(
                                        'CONTENT_STUDIO.PICK_PRODUCTS_HINT',
                                        'Select one or more to group them into a node',
                                    )}
                                </p>
                            </div>
                            <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('CONTENT_STUDIO.CANCEL', 'Cancel')}>
                                <X />
                            </Button>
                        </div>

                        {/* Search + filters */}
                        <div className="space-y-2 px-4 py-3">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={
                                            t('CONTENT_STUDIO.SEARCH_PRODUCTS', 'Search by name or SKU…') as string
                                        }
                                        className="h-9 ps-8"
                                    />
                                </div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="h-9 gap-1.5 px-2.5"
                                            aria-label={t('CONTENT_STUDIO.FILTERS', 'Filters') as string}
                                        >
                                            <ListFilter className="size-4" />
                                            {filterCount > 0 && <span className="text-xs">{filterCount}</span>}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    {/* Portalled outside the panel: without this, a pick in
                                        the menu reads as an outside click and closes the panel. */}
                                    <DropdownMenuContent
                                        align="end"
                                        className="max-h-80 w-56 overflow-y-auto"
                                        data-picker-filter-menu
                                    >
                                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                                            {t('CONTENT_STUDIO.FILTER_PRODUCT_TYPE', 'Product type')}
                                        </DropdownMenuLabel>
                                        {facets.productTypes.length === 0 ? (
                                            <DropdownMenuLabel className="font-normal text-muted-foreground">—</DropdownMenuLabel>
                                        ) : facets.productTypes.map((value) => (
                                            <DropdownMenuCheckboxItem
                                                key={`type-${value}`}
                                                checked={productTypes.includes(value)}
                                                onCheckedChange={() => toggleIn(setProductTypes, value)}
                                                onSelect={(e) => e.preventDefault()}
                                            >
                                                {value}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                                            {t('CONTENT_STUDIO.FILTER_COLLECTION', 'Collection')}
                                        </DropdownMenuLabel>
                                        {facets.collections.length === 0 ? (
                                            <DropdownMenuLabel className="font-normal text-muted-foreground">—</DropdownMenuLabel>
                                        ) : facets.collections.map((value) => (
                                            <DropdownMenuCheckboxItem
                                                key={`collection-${value}`}
                                                checked={collections.includes(value)}
                                                onCheckedChange={() => toggleIn(setCollections, value)}
                                                onSelect={(e) => e.preventDefault()}
                                            >
                                                {value}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                        {filterCount > 0 && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={() => { setProductTypes([]); setCollections([]); }}>
                                                    {t('CONTENT_STUDIO.CLEAR_FILTERS', 'Clear filters')}
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            {families.length > 1 && (
                                <div className="flex flex-wrap gap-1.5">
                                    <FilterChip
                                        label={t('CONTENT_STUDIO.FILTER_ALL', 'All') as string}
                                        selected={!familyId}
                                        onClick={() => chooseFamily(undefined)}
                                    />
                                    {families.map((family) => (
                                        <FilterChip
                                            key={family._id}
                                            label={rt(family.name)}
                                            selected={familyId === family._id}
                                            onClick={() => chooseFamily(family._id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Grid */}
                        <div className="flex-1 overflow-x-hidden overflow-y-auto px-4 pb-2">
                            {loading ? (
                                <div className="flex h-full items-center justify-center">
                                    <Loader2 className="size-5 animate-spin text-muted-foreground" />
                                </div>
                            ) : pageItems.length === 0 ? (
                                <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                                    {t('CONTENT_STUDIO.NO_PRODUCTS', 'No products match.')}
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-2.5 p-0.5">
                                    {pageItems.map((p) => {
                                        const isSelected = !!selected[p.uuid];
                                        return (
                                            <div
                                                key={p.uuid}
                                                onMouseDown={(e: React.MouseEvent) => handleCardMouseDown(e, p)}
                                                className={cn(
                                                    'cursor-grab rounded-lg border-[1.5px] p-1 transition-colors active:cursor-grabbing',
                                                    isSelected
                                                        ? 'border-foreground bg-accent'
                                                        : 'border-transparent hover:border-border',
                                                )}
                                            >
                                                {/* Square thumbnail — aspect-ratio enforced */}
                                                <div
                                                    style={{
                                                        backgroundImage: p.thumbnailPath
                                                            ? `url(${p.thumbnailPath})`
                                                            : undefined,
                                                    }}
                                                    className="relative aspect-square w-full overflow-hidden rounded-md bg-muted bg-cover bg-center"
                                                >
                                                    {isSelected && (
                                                        <span className="absolute end-1 top-1 z-[2] flex size-[18px] items-center justify-center rounded-full bg-foreground text-background">
                                                            <Check className="size-2.5" />
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="mt-1 truncate px-0.5 text-xs leading-tight font-medium text-foreground">
                                                    {p.name}
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between border-t px-4 py-2.5 text-xs font-medium text-muted-foreground">
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.PREVIOUS') as string}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >
                                    <ChevronLeft className="rtl:rotate-180" />
                                </Button>
                                <span className="select-none">
                                    {page} / {totalPages}
                                    <span className="ms-1.5 text-muted-foreground">
                                        · {total} {t('CONTENT_STUDIO.ITEMS', 'items')}
                                    </span>
                                </span>
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    aria-label={t('PAGES.MY_PRODUCTS.PAGINATION.NEXT') as string}
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                >
                                    <ChevronRight className="rtl:rotate-180" />
                                </Button>
                            </div>
                        )}

                        {/* Footer action */}
                        <div className="flex gap-2 border-t bg-muted px-4 py-3">
                            {selectedCount > 0 && (
                                <Button
                                    variant="outline"
                                    className="h-9 whitespace-nowrap"
                                    onClick={() => setSelected({})}
                                >
                                    {t('CONTENT_STUDIO.UNSELECT_ALL', 'Unselect all')}
                                </Button>
                            )}
                            <Button
                                className="h-9 flex-1"
                                disabled={selectedCount === 0}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (selectedCount > 0) {
                                        onDrop(Object.values(selected));
                                        setSelected({});
                                    }
                                }}
                            >
                                {selectedCount === 0
                                    ? replacing
                                        ? t('CONTENT_STUDIO.SELECT_TO_REPLACE', 'Select a product to replace')
                                        : t('CONTENT_STUDIO.SELECT_TO_ADD', 'Select products to add')
                                    : replacing
                                    ? t('CONTENT_STUDIO.REPLACE_PRODUCT', 'Replace product')
                                    : selectedCount === 1
                                    ? t('CONTENT_STUDIO.ADD_TO_CANVAS', 'Add to canvas')
                                    : t('CONTENT_STUDIO.ADD_N_TO_CANVAS', `Add ${selectedCount} as group`, { count: selectedCount })}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>

        {/* Floating drag ghost — follows cursor during custom drag */}
        {dragProduct && (
            <div
                style={{
                    left: dragPos.x - 32,
                    top: dragPos.y - 32,
                    backgroundImage: dragProduct.thumbnailPath
                        ? `url(${dragProduct.thumbnailPath})`
                        : undefined,
                }}
                className="pointer-events-none fixed z-[9999] size-16 rounded-lg border-2 border-foreground bg-muted bg-cover bg-center opacity-90"
            />
        )}
        </>
    );
};
